import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, setFlag } from '../core/save.js';
import { flagObjective } from '../core/quests.js';
import { spendForAction } from '../core/actions.js';
import { OrderRush } from '../minigames/orderRush.js';

// The Toasted Table — downtown's restaurant, home of Order Rush. Bread Boi walks
// the dining room, then steps up to the kitchen pass to work the dinner rush
// (the assemble-to-order minigame). States: 'explore' → 'playing' → 'results'.

export function createRestaurantScene(){
  return () => new RestaurantScene();
}

const PASS = new THREE.Vector3(0, 0, -3);

class RestaurantScene {
  constructor(){
    this.state = 'explore';
    this.rush = null;
    this.panels = [];
    this.chefBobT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x2C1F1A);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 10;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildRoom();
    this._buildKitchen();
    this._buildDiners();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: PASS.clone(), r: 2.3, label: '🔔 work the dinner rush', action: () => this._startRush() },
      { pos: new THREE.Vector3(0, 0, 6.2), r: 2.2, label: '← back to downtown', action: () => this.manager.goTo('downtown', { from: 'restaurant' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFE7C8, 0.7));
    const key = new THREE.DirectionalLight(0xFFF2D8, 0.55);
    key.position.set(3, 9, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);
    const warm = new THREE.PointLight(0xF49B33, 0.5, 14); warm.position.set(0, 3, 1); this.scene.add(warm);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), new THREE.MeshLambertMaterial({ color: 0x7A4A2E }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(16, 8), new THREE.MeshLambertMaterial({ color: 0x5A3A28 }));
    back.position.set(0, 4, -7); back.receiveShadow = true; this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-8, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(8, 4, 0); this.scene.add(sideR);

    const marquee = textSprite('The Toasted Table', 30, '#FFF4DF', 4.8, 1.1);
    marquee.position.set(0, 3.5, -6.7); this.scene.add(marquee);

    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 6.9, this.scene);
    const sign = textSprite('← to downtown', 24, '#FFF4DF', 2.8, 0.8);
    sign.position.set(0, 2.9, 6.7); this.scene.add(sign);
  }

  _buildKitchen(){
    // the pass / counter you work behind
    const g = new THREE.Group(); g.position.set(0, 0, -5); this.scene.add(g);
    box(7, 1.1, 1.0, C.crust, 0, 0.55, 0, g);
    box(7.2, 0.12, 1.2, C.crustDark, 0, 1.15, 0, g);
    // a warming lamp + a couple of plates on the pass
    box(4, 0.1, 0.3, 0xF4C430, 0, 1.9, -0.3, g);
    cyl(0.3, 0.3, 0.05, C.white, -1.5, 1.24, 0, g, 14);
    cyl(0.3, 0.3, 0.05, C.white, 1.5, 1.24, 0, g, 14);

    // the chef behind the pass
    const chef = new THREE.Group(); chef.position.set(0, 0, -6); this.scene.add(chef);
    box(0.9, 1.2, 0.4, C.crust, 0, 1.0, 0, chef);
    box(0.78, 1.0, 0.32, C.cream, 0, 1.02, 0.06, chef);
    sphere(0.055, 0x2E2018, -0.16, 1.25, 0.22, chef, 8);
    sphere(0.055, 0x2E2018,  0.16, 1.25, 0.22, chef, 8);
    cyl(0.27, 0.3, 0.34, C.white, 0, 1.78, 0, chef, 14);        // chef hat band
    sphere(0.34, C.white, 0, 2.02, 0, chef, 14);                // hat puff
    this.chef = chef; this.chefBaseY = 0;
  }

  _buildDiners(){
    // dining tables with seated customers (decor; the tickets are the "orders")
    const table = (x, z) => {
      cyl(0.08, 0.08, 0.9, C.crustDark, x, 0.45, z, this.scene, 8);
      cyl(0.7, 0.7, 0.1, C.crust, x, 0.9, z, this.scene, 16);
      const cols = [C.raspberry, C.cobalt, C.sprout, C.tangerine];
      [[0.9, 0], [-0.9, 0]].forEach(([dx, dz], i) => {
        cyl(0.22, 0.22, 0.5, C.rye, x + dx, 0.25, z + dz, this.scene, 10);   // stool
        const c = sphere(0.32, cols[(Math.abs(x + z + i) | 0) % cols.length], x + dx, 1.15, dz + z, this.scene, 12);
        c.scale.set(1, 1.2, 1);
      });
    };
    table(-4, 1.5); table(4, 1.5); table(-4, -1.5); table(4, -1.5);
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.66];
    this._toExplore(true);
  }

  exit(){
    this._teardownGame();
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._exploreTap(ray);
    HUD.setSubtitle('the toasted table');
    if (first) HUD.say('The Toasted Table! Grab a look around, then step up to the pass to work the dinner rush.');
    HUD.setHint('click to walk · walk up to the kitchen pass or the exit');
    HUD.hideHintSoon(7000);
  }

  _exploreTap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6.5, Math.min(6.5, gp.x));
    gp.z = Math.max(-2.4, Math.min(6.4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _startRush(){
    const check = spendForAction('orderRush');
    if (!check.ok){
      HUD.say(`Too tired for a dinner rush. ${check.reason}`);
      return;
    }
    this.state = 'playing';
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');

    this.bread.stop();
    this.bread.root.position.set(0, 0, -1.4);
    this.bread.root.rotation.y = Math.PI;

    this.rush = new OrderRush({
      onServe: () => { this.chefBobT = 0.4; this.bread.drawFace('grin'); },
      onFinish: (r) => this._results(r)
    });
    this.rush.mount();
    this.rush.begin();

    const q = this._panel('<button class="cafe-quit">✕ leave</button>', 'bare');
    q.querySelector('.cafe-quit').onclick = () => { this._teardownGame(); this._toExplore(false); };
  }

  _results(r){
    this.state = 'results';
    this._teardownGame();
    this.bread.drawFace('yum');
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    setFlag('completedOrderRush', true);
    flagObjective('dinner-rush', 'completedOrderRush');
    HUD.refreshCrumbs();
    HUD.refreshEnergy();

    const grade = r.rating >= 95 ? 'S' : r.rating >= 85 ? 'A' : r.rating >= 70 ? 'B' : r.rating >= 50 ? 'C' : 'D';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Service done!</h2>
        <div class="cafe-stats">
          <div><b>${r.served}</b><span>served</span></div>
          <div><b>${r.walkouts}</b><span>walkouts</span></div>
          <div><b>${r.bestCombo}</b><span>best combo</span></div>
          <div><b>${r.rating}%</b><span>rating</span></div>
        </div>
        <p class="cafe-payout">🍞 +${r.crumbs} crumbs</p>
        <button class="cafe-btn again">↻ another rush</button>
        <button class="cafe-btn ghost leave">← back to the floor</button>
      </div>`);
    p.querySelector('.again').onclick = () => this._startRush();
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _teardownGame(){ if (this.rush){ this.rush.unmount(); this.rush = null; } }

  update(dt){
    if (this.chefBobT > 0){
      this.chefBobT -= dt;
      this.chef.position.y = this.chefBaseY + Math.sin((0.4 - this.chefBobT) * Math.PI / 0.4) * 0.18;
    } else this.chef.position.y = this.chefBaseY;

    if (this.state === 'playing'){
      this.rush.update(dt);
      this.camera.position.set(3.4, 3.9, 6.2);
      this.camera.lookAt(0, 1.4, -2.5);
      return;
    }

    this.bread.update(dt);
    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
